import { CodeKeyName, createCodeKeyName } from '@ukam/core/models/code-key-name';
import { createRefField, RefField } from '@ukam/core/models/ref-field';
import { createStatusField, StatusField } from '@ukam/core/models/status-field';
import { createRecordLog, RecordLog } from '@ukam/core/models/record-log';
import { hydrate } from '@ukam/core/functions/object.functions';

export interface StoredFile {
  id?: string | null;
  nid?: number | null;
  code?: number | null;
  key?: string | null;
  storage?: RefField | null;
  feature?: CodeKeyName | null;
  parentNid?: number | null;
  /** Código público da entidade dona (espelha StoredFile.ParentCode). */
  parentCode?: number | null;
  /** Nome exibido da entidade dona (ex.: número do contrato do empréstimo). */
  parentName?: string | null;
  /** Documento da entidade dona (PersonDocumentType: cpf | cnpj). */
  parentDocumentType?: CodeKeyName | null;
  parentDocumentNumber?: string | null;
  /** Tipo de negócio do documento (StoredFileType). */
  type?: CodeKeyName | null;
  /** user | system: enviado pelo usuário ou gerado pelo sistema. */
  origin?: CodeKeyName | null;
  /** Grupo (StoredFileGroup); code 0 = sem grupo. Grupos pertencem à feature dona. */
  group?: CodeKeyName | null;
  /** Pasta lógica (StoredFolder). nid 0 = raiz. */
  folder?: RefField | null;
  /** Caminho por nid da pasta, "/12/57/". */
  folderPath?: string | null;
  originalName?: string | null;
  description?: string | null;
  note?: string | null;
  extension?: string | null;
  contentType?: string | null;
  sizeBytes?: number | null;
  version?: number | null;
  isCurrent?: boolean | null;
  /** public | private | restricted */
  visibility?: CodeKeyName | null;
  /** URL pública permanente; só preenchida quando visibility = public e o arquivo está disponível. */
  publicUrl?: string | null;
  status?: StatusField | null;
  expiresAt?: string | null;
  retentionUntil?: string | null;
  purgeScheduledAt?: string | null;
  purgedAt?: string | null;
  company?: RefField | null;
  log?: RecordLog | null;
}

export function createStoredFile(overrides: Partial<StoredFile> = {}): StoredFile {
  const defaults: StoredFile = {
    id: null, nid: null, code: null, key: null,
    storage: createRefField(),
    feature: createCodeKeyName(), parentNid: null, parentCode: null, parentName: null, parentDocumentType: createCodeKeyName(), parentDocumentNumber: null, type: createCodeKeyName(), origin: createCodeKeyName(), group: createCodeKeyName(), folder: createRefField(), folderPath: null,
    originalName: null, description: null, note: null, extension: null, contentType: null,
    sizeBytes: null, version: 1, isCurrent: true,
    visibility: createCodeKeyName(), publicUrl: null,
    status: createStatusField(),
    expiresAt: null, retentionUntil: null, purgeScheduledAt: null, purgedAt: null,
    company: createRefField(), log: createRecordLog(),
  };
  return hydrate(defaults, overrides);
}

export interface StoredFileUploadIntentResult {
  file: StoredFile;
  uploadUrl: string;
  expiresAt: string;
  headers: Record<string, string>;
}

export interface StoredFileDownloadUrlResult {
  id: string;
  url: string;
  expiresAt: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
}

export const STORED_FILE_ORIGIN = {
  user: 'user',
  system: 'system',
} as const;

export const STORED_FILE_VISIBILITY = {
  public: 'public',
  private: 'private',
  restricted: 'restricted',
} as const;

export const STORED_FILE_STATUS = {
  pending: 'pending',
  available: 'available',
  superseded: 'superseded',
  deleted: 'deleted',
  pendingPurge: 'pending_purge',
  purged: 'purged',
  expired: 'expired',
} as const;
